import { LoadingSpinner } from "@bluefalcon/components-package";
import { HelpTicket, HelpTicketCreateRequest, HelpTicketSearchRequest, HelpTicketUpdateRequest } from "@bluefalcon/clients-management-client-fetch";
import { Box, Button, Checkbox, Flex, Input, Table, TableCaption, Tbody, Td, Th, Thead, Tr, useDisclosure } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { helpTicketService } from "../../services";
import { formatISOToFrontendDate } from "@bluefalcon/utils-package";
import { AddEditHelpTicketModal } from "../../modals/add-edit-help-ticket.modal";
import { Link } from "react-router-dom";

type HelpTicketsTableProps = {
    searchOptions?: HelpTicketSearchRequest;
};

export const HelpTicketsTable = ({
    searchOptions,
}: HelpTicketsTableProps) => {

    const [helpTickets, setHelpTickets] = useState<HelpTicket[]>([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const { isOpen, onClose, onOpen } = useDisclosure();
    const [selectedTicket, setSelectedTicket] = useState<HelpTicket>();
    const [query, setQuery] = useState('');

    const fetchHelpTickets = async () => {
        setIsLoading(true);
        try {
            const { results, total: _total } = await helpTicketService.searchHelpTickets({
                query,
                ...searchOptions,
            });
            setHelpTickets(orderByCreatedDate(results));
            setTotal(_total);
        } catch (error) {

        } finally {
            setIsLoading(false);
        }
    };

    const orderByCreatedDate = (data: HelpTicket[]) => {
        return data.sort((a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime());
    };

    const handleCreate = async (data: HelpTicketCreateRequest) => {
        await helpTicketService.createHelpTicket(data);
        await fetchHelpTickets();
    };

    const handleEdit = async (id: number, data: HelpTicketUpdateRequest) => {
        await helpTicketService.updateHelpTicket(id, data);
        await fetchHelpTickets();
    };

    const handleResolve = async (id: number, value: boolean) => {
        const toChange = helpTickets.find((t) => t.id === id);
        if (toChange) {
            const tickets = helpTickets.filter((t) => t.id !== id);
            setHelpTickets(() => (orderByCreatedDate([...tickets, { ...toChange, resolved: value }])));
            const ticket = await helpTicketService.updateHelpTicket(id, { resolved: value });
            const newTickets = helpTickets.filter((t) => t.id !== ticket.id);
            setHelpTickets(() => (orderByCreatedDate([...newTickets, ticket])));
        }
    };

    const handleClickEdit = (helpTicket: HelpTicket) => {
        setSelectedTicket(helpTicket);
        onOpen();
    };

    const handleAddButtonClick = () => {
        onOpen();
        setSelectedTicket(undefined);
    };

    const search = () => {

    };

    useEffect(() => {
        fetchHelpTickets();
    }, []);


    if (isLoading) {
        return <LoadingSpinner />
    }

    return (
        <>
            <Flex mb={2}>
                <form onSubmit={search}>
                    <Flex>
                        <Input onChange={(e) => setQuery(e.target.value)} value={query} placeholder="search..." type={'search'} />
                        <Button type="submit" onClick={search} ml={2} colorScheme="primary">Search</Button>
                    </Flex>
                </form>
                <Button onClick={handleAddButtonClick} ml={'auto'} colorScheme="teal">Create Ticket</Button>
            </Flex>
            {(!isLoading && helpTickets.length > 0) ? <Table variant="simple">
                <TableCaption textAlign={'left'}>Total Tickets: {total}</TableCaption>
                <Thead>
                    <Tr>
                        <Th>ID</Th>
                        <Th>Title</Th>
                        <Th>Details</Th>
                        <Th>Resolved Date</Th>
                        <Th>From</Th>
                        <Th>Resolved</Th>
                        <Th>Actions</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {helpTickets.map((helpTicket) => (
                        <Tr key={helpTicket.id}>
                            <Td>{helpTicket.id}</Td>
                            <Td>{helpTicket.title}</Td>
                            <Td>{helpTicket.details}</Td>
                            <Td>{formatISOToFrontendDate(helpTicket.resolvedDate) ?? ''}</Td>
                            <Td>
                                <Box to={`/users/${helpTicket.fromUserId}`} as={Link}>
                                    {helpTicket.fromUser.email}
                                </Box>
                            </Td>
                            <Td>
                                <Checkbox isChecked={helpTicket.resolved} onChange={() => handleResolve(helpTicket.id, !helpTicket.resolved)} />
                            </Td>
                            <Td>
                                <Button size="sm" onClick={() => handleClickEdit(helpTicket)}>
                                    Edit
                                </Button>
                            </Td>
                        </Tr>
                    ))}
                </Tbody>
            </Table> : <Box textAlign={'center'} mt={4}>
                Inbox is Empty.
            </Box>}
            <AddEditHelpTicketModal
                isOpen={isOpen}
                onClose={onClose}
                onDelete={fetchHelpTickets}
                handleAddHelpTicket={handleCreate}
                handleEditHelpTicket={handleEdit}
                helpTicket={selectedTicket}
            />
        </>
    )
};
